<template>
  <v-navigation-drawer
    :model-value="modelValue"
    location="right"
    temporary
    width="760"
    class="ncrm-contact-normalization-drawer"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="d-flex flex-column fill-height">
      <div class="pa-4 ncrm-contact-normalization-drawer__header">
        <div class="d-flex align-start justify-space-between ga-3">
          <div class="min-w-0">
            <div class="text-h6 ncrm-text-strong">
              {{ record?.company_name || $t("ncrm.workflows.contactNormalization.drawer.unknownCompany") }}
            </div>
            <div class="d-flex flex-wrap align-center ga-2 mt-2">
              <v-chip v-if="record?.status" size="x-small" :color="statusColor(record.status)">
                {{ $t(`ncrm.workflows.contactNormalization.status.${record.status}`) }}
              </v-chip>
              <v-chip size="x-small" color="neutral">
                {{ nodeLabel }}
              </v-chip>
            </div>
          </div>
          <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('update:modelValue', false)" />
        </div>
      </div>
      <v-divider />
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <div class="flex-grow-1 overflow-y-auto pa-4">
        <template v-if="record">
          <v-alert
            v-if="record.status === 'failed'"
            type="warning"
            density="compact"
            class="mb-4"
          >
            {{ $t("ncrm.workflows.contactNormalization.drawer.failedHint") }}
          </v-alert>

          <div class="d-flex flex-column ga-3">
            <div
              v-for="field in fieldRows"
              :key="field.key"
              class="ncrm-contact-normalization-drawer__field"
            >
              <div class="text-caption font-weight-semibold text-medium-emphasis text-uppercase">
                {{ field.label }}
              </div>
              <div class="ncrm-contact-normalization-drawer__values mt-2">
                <div>
                  <div class="ncrm-contact-normalization-drawer__value-label">
                    {{ $t("ncrm.workflows.contactNormalization.drawer.originalValue") }}
                  </div>
                  <div class="ncrm-contact-normalization-drawer__value">
                    {{ valueLabel(record.original_values?.[field.key]) }}
                  </div>
                </div>
                <v-icon size="14" color="medium-emphasis">mdi-arrow-right</v-icon>
                <v-text-field
                  v-model="form[field.key]"
                  :label="$t('ncrm.workflows.contactNormalization.drawer.proposedValue')"
                  density="compact"
                  hide-details="auto"
                  :disabled="saving || applying || record.status === 'applied'"
                />
              </div>
            </div>
          </div>

          <v-alert
            v-if="issues.length"
            type="info"
            density="compact"
            class="mt-4"
          >
            <div class="font-weight-medium mb-1">
              {{ $t("ncrm.workflows.contactNormalization.drawer.issuesTitle") }}
            </div>
            <div v-for="issue in issues" :key="`${issue.code}-${issue.field || issue.count || issue.value}`">
              {{ issue.message || issue.code }}
            </div>
          </v-alert>

          <v-textarea
            v-model="note"
            class="mt-4"
            :label="$t('ncrm.workflows.contactNormalization.drawer.operatorNote')"
            rows="3"
            hide-details="auto"
            :disabled="saving || applying || record.status === 'applied'"
          />
        </template>
      </div>

      <v-divider />
      <div class="pa-4 d-flex flex-wrap justify-end ga-2">
        <v-btn variant="text" @click="$emit('update:modelValue', false)">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn
          v-if="record && record.status !== 'applied'"
          color="warning"
          variant="tonal"
          :disabled="saving || applying"
          @click="$emit('skip', { record, note })"
        >
          <v-icon start>mdi-debug-step-over</v-icon>
          {{ $t("ncrm.workflows.contactNormalization.actions.skip") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="tonal"
          :disabled="!record || saving || applying || record.status === 'applied'"
          :loading="saving"
          @click="submitCorrection"
        >
          <v-icon start>mdi-content-save-outline</v-icon>
          {{ $t("ncrm.workflows.contactNormalization.actions.saveCorrection") }}
        </v-btn>
        <v-btn
          color="success"
          :disabled="!canApply"
          :loading="applying"
          @click="$emit('apply', record)"
        >
          <v-icon start>mdi-check-circle-outline</v-icon>
          {{ $t("ncrm.workflows.contactNormalization.actions.applyRow") }}
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { CONTACT_NORMALIZATION_NODE_TYPES } from "@/Utile/ncrmWorkflows.mjs";

const PHONE_FIELDS = ["phone_primary", "phone_secondary", "phone_tertiary"];
const EMAIL_FIELDS = ["email_primary", "email_secondary", "email_tertiary"];

export default {
  name: "NcrmContactNormalizationDrawer",
  props: {
    modelValue: { type: Boolean, default: false },
    record: { type: Object, default: null },
    nodeType: { type: String, default: CONTACT_NORMALIZATION_NODE_TYPES.PHONE },
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    applying: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "save", "apply", "skip"],
  data() {
    return {
      form: {},
      note: "",
    };
  },
  computed: {
    fields() {
      return this.nodeType === CONTACT_NORMALIZATION_NODE_TYPES.EMAIL ? EMAIL_FIELDS : PHONE_FIELDS;
    },
    fieldRows() {
      return this.fields.map((key) => ({
        key,
        label: this.$t(`ncrm.workflows.contactNormalization.fields.${key}`),
      }));
    },
    nodeLabel() {
      return this.nodeType === CONTACT_NORMALIZATION_NODE_TYPES.EMAIL
        ? this.$t("ncrm.workflows.nodes.emailNormalization.label")
        : this.$t("ncrm.workflows.nodes.phoneNormalization.label");
    },
    issues() {
      return Array.isArray(this.record?.issues) ? this.record.issues : [];
    },
    canApply() {
      return Boolean(
        this.record?.status === "ready" &&
        !this.saving &&
        !this.applying
      );
    },
  },
  watch: {
    record: {
      immediate: true,
      deep: true,
      handler() {
        this.resetForm();
      },
    },
    nodeType() {
      this.resetForm();
    },
  },
  methods: {
    resetForm() {
      const proposed = this.record?.proposed_values || {};
      this.form = this.fields.reduce((acc, field) => {
        acc[field] = proposed[field] ?? "";
        return acc;
      }, {});
      this.note = "";
    },
    valueLabel(value) {
      const normalized = value === null || value === undefined ? "" : String(value).trim();
      return normalized || this.$t("ncrm.workflows.contactNormalization.drawer.emptyValue");
    },
    statusColor(status) {
      if (status === "ready") return "success";
      if (status === "needs_review") return "warning";
      if (status === "applied") return "primary";
      if (status === "failed") return "error";
      return "neutral";
    },
    submitCorrection() {
      if (!this.record) return;
      this.$emit("save", {
        record: this.record,
        proposed_values: { ...this.form },
        note: this.note,
      });
    },
  },
};
</script>

<style scoped>
.ncrm-contact-normalization-drawer__header {
  background: rgb(var(--v-theme-surface));
}

.ncrm-contact-normalization-drawer__field {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 12px;
}

.ncrm-contact-normalization-drawer__values {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}

.ncrm-contact-normalization-drawer__value-label {
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.72rem;
}

.ncrm-contact-normalization-drawer__value {
  overflow-wrap: anywhere;
  font-size: 0.9rem;
  margin-top: 2px;
}

@media (max-width: 720px) {
  .ncrm-contact-normalization-drawer__values {
    grid-template-columns: 1fr;
  }
}
</style>
